import React, {useEffect, useRef, ReactNode, useState} from 'react';
import _ from 'lodash';
import {useNodeCallbackContext, NodeLocationInfo, NodeLocationBall} from 'Contexts/NodeCallBackContext';
import {AstPosition, isNodeInsideCore, AstView, findRootsOfSelection} from 'rendition-core';
import {useHighlighterContext} from 'Contexts/HighlighterContext';

export const DragSelector: React.FC<{
    view: AstView;
    children: ReactNode;
    onNodeSelection?: (id: string[]) => void;
    isSpaceKeyPressed?: boolean;
}> = ({onNodeSelection, children, view, isSpaceKeyPressed}) => {
    // onMouseDown
    // onMouseUp
    // get box
    // use box to find selected nodes.
    const {clearHighlightByType, addNodeHighlight} = useHighlighterContext();

    const containerRef = useRef<HTMLDivElement | null>(null);

    const highlightRef = useRef<HTMLDivElement | null>(null);

    const {nodeCallbacks, getNodesCurrentLocations} = useNodeCallbackContext();
    const mouseDown = useRef(false);
    const dragging = useRef(false);

    const startingPoint = useRef<{x: number; y: number}>({x: 0, y: 0});

    const mousePositionRef = useRef({x: 0, y: 0});

    const nodeLocations = useRef<NodeLocationBall>({});

    const [highLightedNodeIds, setHighLightedNodeIds] = useState<string[]>([]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const calculateSelectedNodes = (box: {start: {x: number; y: number}; end: {x: number; y: number}}) => {
            const mouseBox = calculateMouseBox(box);

            if (mouseBox.width < 4 && mouseBox.height < 4) {
                return;
            } else {
                const selected: string[] = [];
                Object.keys(nodeLocations.current).forEach((id) => {
                    const location = nodeLocations.current[id];
                    if (isNodeInsideBox(mouseBox, location)) {
                        selected.push(id);
                    }
                });

                return selected;
            }
        };

        function onMouseDown(ev: MouseEvent) {
            if (ev.button !== 0 || isSpaceKeyPressed === true) {
                return;
            }
            mouseDown.current = true;
            mousePositionRef.current = {x: ev.clientX, y: ev.clientY};
            startingPoint.current = {...mousePositionRef.current};
        }

        function onMouseUp(e: MouseEvent) {
            mouseDown.current = false;
            if (dragging.current) {
                dragging.current = false;

                const selected = calculateSelectedNodes({start: startingPoint.current, end: mousePositionRef.current});

                if (onNodeSelection && selected) {
                    onNodeSelection(selected);
                }

                if (highlightRef.current) {
                    highlightRef.current.style.display = 'none';
                }
            }
        }

        function clearDrag() {
            mouseDown.current = false;
            if (dragging.current) {
                dragging.current = false;

                const selected = calculateSelectedNodes({start: startingPoint.current, end: mousePositionRef.current});

                if (onNodeSelection && selected) {
                    onNodeSelection(selected);
                }

                if (highlightRef.current) {
                    highlightRef.current.style.display = 'none';
                }
            }
        }

        function mouseMove(ev: MouseEvent) {
            if (mouseDown.current) {
                if (isSpaceKeyPressed === true) {
                    return;
                }

                mousePositionRef.current = {x: ev.clientX, y: ev.clientY};

                const box = calculateMouseBox({
                    start: {x: startingPoint.current.x, y: startingPoint.current.y},
                    end: {x: mousePositionRef.current.x, y: mousePositionRef.current.y},
                });
                // start the drag
                if (!dragging.current && box.width > 4 && box.height > 4) {
                    nodeLocations.current = getNodesCurrentLocations();
                    clearHighlightByType('ALL');

                    dragging.current = true;
                }

                if (highlightRef.current && dragging.current) {
                    if (dragging.current) {
                        const selected = calculateSelectedNodes({
                            start: startingPoint.current,
                            end: mousePositionRef.current,
                        });

                        if (selected) {
                            const nodesToHighlight = findRootsOfSelection(view, selected);
                            setHighLightedNodeIds((old) => {
                                const newSet = nodesToHighlight.map((node) => node.internalId).sort();
                                if (!_.isEqual(old, newSet)) {
                                    return newSet;
                                }
                                return old;
                            });
                        }

                        highlightRef.current.style.display = 'block';
                        highlightRef.current.style.width = box.width + 'px';
                        highlightRef.current.style.height = box.height + 'px';
                        highlightRef.current.style.top = box.absoluteY + 'px';
                        highlightRef.current.style.left = box.absoluteX + 'px';
                    }
                }
            }
        }

        containerRef.current.addEventListener('mousemove', mouseMove);
        containerRef.current.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mouseup', onMouseUp);
        window.addEventListener('blur', clearDrag);

        const refToRef = containerRef.current;

        return () => {
            refToRef.removeEventListener('mousemove', mouseMove);
            refToRef.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('blur', clearDrag);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onNodeSelection, nodeCallbacks, isSpaceKeyPressed]);

    useEffect(() => {
        clearHighlightByType('Regroup');
        highLightedNodeIds.forEach((id) => {
            addNodeHighlight(id, 'Regroup');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highLightedNodeIds]);

    return (
        <div
            style={{
                position: 'relative',
                height: '100%',
            }}
            ref={containerRef}
        >
            <div
                style={{
                    position: 'fixed',
                    zIndex: 100,
                    border: '1px solid blue',
                }}
                ref={highlightRef}
            >
                <div
                    style={{
                        backgroundColor: 'blue',
                        opacity: 0.1,
                        height: '100%',
                        width: '100%',
                    }}
                ></div>
            </div>
            {children}
        </div>
    );
};

const isNodeInsideBox = (mouseBox: AstPosition, location: NodeLocationInfo) => {
    const actualLocation = location.actual;

    const result = isNodeInsideCore(
        {
            absoluteX: actualLocation.x,
            absoluteY: actualLocation.y,
            width: actualLocation.width,
            height: actualLocation.height,
        },
        mouseBox
    );

    return result.inside;
};

const calculateMouseBox = (box: {start: {x: number; y: number}; end: {x: number; y: number}}) => {
    const topLeft = {x: Math.min(box.start.x, box.end.x), y: Math.min(box.start.y, box.end.y)};
    const bottomRight = {x: Math.max(box.start.x, box.end.x), y: Math.max(box.start.y, box.end.y)};

    return {
        absoluteX: topLeft.x,
        absoluteY: topLeft.y,
        width: bottomRight.x - topLeft.x,
        height: bottomRight.y - topLeft.y,
    };
};
